/**
 * CardMaterialUnified - Composant unifié pour MATERIALS et PROJETS
 * 
 * @prop {object} material - Données du material/projet
 * @prop {object} context - Contexte ManageMaterials
 * @prop {function} altT - Traduction alternative
 * @prop {array} templatesWidgetList - Templates disponibles
 * @prop {object} customStatusConfig - Configuration des statuts (pour utilisation autre variant material/project)
 * @prop {array} statusOrder - Ordre des statuts
 * @prop {function} saveCb - Callback après sauvegarde (pour utilisation autre material/project)
 * 
 * VARIANT "material" :
 * - Structure plate (1 niveau)
 * - Layout : Header → Pipeline → [Actions | Files]
 * - Fichiers depuis material.materialFiles
 * 
 * VARIANT "project" :
 * - Structure hiérarchique (Projet → Personnes → Versions)
 * - Layout : Header → [Sidebar | Main avec tabs versions]
 * - Fichiers depuis currentVersion.materialFiles
 */

import React from 'react';
import moment from 'moment';
import { Badge, Button, Alert } from "reactstrap";
import WidgetBreadcrumb from 'components/WidgetBreadcrumb';
import FilesCustom from 'components/FilesCustom';
import * as commonFunc from 'scripts/common';
import Swal from "sweetalert2";

// Import des fonctions existantes
import { saveMaterial, showDialogMaterial, dialogAltMaterial, sendEmailDialog, sendRecapEmailDialog } from '../views/pages/ManageMaterials';

function CardMat(props) {
    const {
        material,
        context,
        t, altT,
        templatesWidgetList,
        customStatusConfig: externalStatusConfig,
        descActions,
        statusOrder,
        hasPrices,
        shouldVisit,
        recall,
        saveCb: externalSaveCb
    } = props;

    const filesCustomRef = React.useRef(null);

    // ========== STATE ==========
    const [collapsed, setCollapsed] = React.useState(true);


    React.useEffect(() => {
        if (props.expandAll === true) setCollapsed(false);
        if (props.expandAll === false) setCollapsed(true);
    }, [props.expandStamp]);

    // State spécifique au mode project
    const [selectedPersonID, setSelectedPersonID] = React.useState(null);
    const [selectedVersionID, setSelectedVersionID] = React.useState(null);

    // ========== LOGIQUE MATERIAL (extras, services) - Avant customStatusConfig ==========
    let statusClass = "";

    // ========== CONFIGURATION STATUS avec Tooltips ==========
    const customStatusConfig = externalStatusConfig || (() => {
        const config = {};
        context.state.status?.forEach(status => {
            // Filtrage selon hasPrices et shouldVisit pour variant material
            if (!hasPrices && status.idStatus === 3003) return;
            if (!shouldVisit && (status.idStatus === 3004 || status.idStatus === 3007)) return;

            config[status.idStatus] = status;
            const statusObj = status;
            if (!statusObj.bgColor && statusObj.css) {
                switch (true) {
                    case statusObj.css.indexOf("purple-300") >= 0: statusObj.bgColor = "#BA68c8"; break;
                    case statusObj.css.indexOf("amber-600") >= 0: statusObj.bgColor = "#ffb300"; break;
                    case statusObj.css.indexOf("cyan-600") >= 0: statusObj.bgColor = "#00acc1"; break;
                    case statusObj.css.indexOf("green-400") >= 0: statusObj.bgColor = "#66bb6a"; break;
                    case statusObj.css.indexOf("bg-danger") >= 0: statusObj.bgColor = "#DC3545"; break;
                    case statusObj.css.indexOf("bg-success") >= 0: statusObj.bgColor = "#198754"; break;
                    case statusObj.css.indexOf("bg-primary") >= 0: statusObj.bgColor = "#0D6EFD"; break;
                    case statusObj.css.indexOf("bg-secondary") >= 0: statusObj.bgColor = "#6C757D"; break;
                    case statusObj.css.indexOf("bg-info") >= 0: statusObj.bgColor = "#0DCAF0"; break;
                    case statusObj.css.indexOf("bg-warning") >= 0: statusObj.bgColor = "#FFC107"; break;
                    default: break;
                }
            }
            if (!statusObj.textColor) statusObj.textColor = statusObj.bgColor;
            let classFa = "";
            if (statusObj.css?.indexOf("fa-")) {
                statusObj.css.split(" ").forEach((classElem) => {
                    classFa += (classElem.startsWith("fa-") ? (classElem + " ") : "");
                });
                if (classFa) statusObj.icon = "fa " + classFa;
            }
        });

        // ========== TOOLTIPS STATUS 3004 (Visite) et 3006 (Perdu) ==========
        if (material.extras) {
            // Tooltip Visite planifiée
            if (config[3004]) {
                const visitExtras = material.extras.map(ext => {
                    if (ext.name === "visiteDate") return commonFunc.formatDateTime(commonFunc.restStringToDate(ext.value), true);
                    if (ext.name === "visitItv") return ext.value;
                    return null;
                }).filter(ext => ext !== null);
                if (visitExtras.length) {
                    config["3004tooltip"] = { title: "Visite", value: visitExtras };
                }
            }

            // Tooltip Prospect perdu
            if (config[3006]) {
                const lossExtras = material.extras.filter(ext => ext.name === "lossReason");
                if (lossExtras.length) {
                    config["3006tooltip"] = { title: "Perdu", value: lossExtras[0].value };
                }
            }
        }

        return config;
    })();

    // Source des données selon le variant
    const dataSource = material;
    const statusHistory = dataSource?.flows;

    // ========== REFRESH MATERIAL (pour project) ==========
    const refreshMaterial = (targetMaterial) => {
        const args = { id: targetMaterial.idMaterial };
        if (context.state.altMode) args.level = 3;

        Swal.fire({
            title: context.props.i18nManager.t("gal_loading"),
            didOpen: () => { Swal.showLoading() },
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false
        });

        commonFunc.getMethod("../api/material/GetMaterial", args).then((response) => {
            const dataNew = response.data;
            if (dataNew.isSuccess) {

                //Ajustement comme GetList
                dataNew.result.parent = null;
                dataNew.result.billingAddress = null;

                Swal.close();

                const idLocalMaterial = parseInt(targetMaterial.idMaterial);
                let flag = -1;
                let breakAll = false;
                const materials = context.state.materials;

                materials.forEach((localMaterial, index) => {
                    if (flag < 0 && !breakAll) {
                        flag = (localMaterial.idMaterial === idLocalMaterial) ? index : -1;
                        if (flag < 0 && localMaterial.persons) {
                            localMaterial.persons?.forEach((person, indexP) => {
                                if (flag < 0 && !breakAll) {
                                    if (person.versions) {
                                        person.versions?.forEach((version, indexV) => {
                                            if (flag < 0) {
                                                flag = (version.idMaterial === idLocalMaterial) ? indexV : -1;
                                            }
                                        });
                                        if (flag >= 0) {
                                            person.versions[flag] = dataNew.result;
                                            person.client = dataNew.result.client;
                                            person.groupID = dataNew.result.groupID;
                                            person.group = dataNew.result.group;
                                            breakAll = true;
                                        } else if (person.parentID === targetMaterial.parentID && person.clientID === targetMaterial.clientID) {
                                            person.versions.push(dataNew.result);
                                            if (person.recall < dataNew.result.recall) person.recall = dataNew.result.recall;
                                            person.client = dataNew.result.client;
                                            person.groupID = dataNew.result.groupID;
                                            person.group = dataNew.result.group;
                                            breakAll = true;
                                            setSelectedVersionID(dataNew.result.idMaterial);
                                        }
                                    }
                                    if (!breakAll) {
                                        flag = (person.idMaterial === idLocalMaterial) ? indexP : -1;
                                    }
                                }
                            });
                            if (!breakAll) {
                                if (flag >= 0) {
                                    const versionsList = localMaterial.persons[flag].versions;
                                    localMaterial.persons[flag] = dataNew.result;
                                    localMaterial.persons[flag].versions = versionsList;
                                    breakAll = true;
                                } else if (localMaterial.idMaterial === targetMaterial.parentID) {
                                    const newVersion = Object.assign({}, dataNew.result);
                                    localMaterial.persons.push(dataNew.result);
                                    if (!dataNew.result.versions) dataNew.result.versions = [];
                                    dataNew.result.versions.push(newVersion);
                                    if (dataNew.result.recall < newVersion.recall) dataNew.result.recall = newVersion.recall;
                                    breakAll = true;
                                    setSelectedPersonID(dataNew.result.idMaterial);
                                    setSelectedVersionID(newVersion.idMaterial);
                                }
                            }
                        } else if (localMaterial.idMaterial === targetMaterial.parentID) {
                            const newVersionP = Object.assign({}, dataNew.result);
                            if (!localMaterial.persons) localMaterial.persons = [];
                            localMaterial.persons.push(dataNew.result);
                            if (!dataNew.result.versions) dataNew.result.versions = [];
                            dataNew.result.versions.push(newVersionP);
                            if (dataNew.result.recall < newVersionP.recall) dataNew.result.recall = newVersionP.recall;
                            breakAll = true;
                            setSelectedPersonID(dataNew.result.idMaterial);
                            setSelectedVersionID(newVersionP.idMaterial);
                        }
                    }
                });

                if (flag >= 0 && !breakAll) {
                    dataNew.result.cRecommandations = materials[flag].cRecommandations;

                    const personsList = materials[flag].persons;
                    materials[flag] = dataNew.result;
                    materials[flag].persons = personsList;
                }
                context.setState({ materials: materials, loading: false, dataStamp: new Date().getTime(), isShowDetails: false });
            } else {
                Swal.fire({
                    icon: "error",
                    title: context.props.i18nManager.t("gal_error"),
                    html: context.props.i18nManager.t("material.failLoadMaterial") + " :<br>" + dataNew.errorMessage
                });
            }
        });
    };

    const saveCb = externalSaveCb || ((targetMaterial) => {
        if (!targetMaterial.idMaterial && targetMaterial.result?.idMaterial) targetMaterial = targetMaterial.result;
        refreshMaterial(targetMaterial);
    });

    // ========== CONFIGURATION FICHIERS ==========
    const fileSource = material;

    // Fichiers non-RCP
    const filesAltObj = {
        ...(context.filesType ? { filesType: context.filesType } : { altFilesTypes: "../api/material/GetMaterialFilesType" }),
        files: fileSource?.materialFiles?.map(file => file.file.categ !== "RCP" ? file.file : null).filter(Boolean) || [],
        setFiles: (cptFiles) => { saveCb(fileSource) }
    };

    // Garder uniquement le DERNIER fichier non-RCP
    if (filesAltObj.files?.length) {
        var sortedFiles = filesAltObj.files.sort((a, b) => new Date(b.dSysMod).getTime() - new Date(a.dSysMod).getTime());
        var tarifFiles = sortedFiles.filter(file => { return file.categ === "PDF"; });
        filesAltObj.files = [tarifFiles[0], ...sortedFiles.filter(file => { return file.categ !== "PDF"; })];
    }

    // Fichiers RCP
    const filesRcpObj = {
        ...(context.filesType ? { filesType: context.filesType } : { altFilesTypes: "../api/material/GetMaterialFilesType" }),
        files: fileSource?.materialFiles?.map(file => file.file.categ === "RCP" ? file.file : null).filter(Boolean) || []
    };

    // Garder uniquement le DERNIER fichier RCP
    if (filesRcpObj.files?.length) {
        filesRcpObj.files = [filesRcpObj.files.sort((a, b) => new Date(b.dSysMod).getTime() - new Date(a.dSysMod).getTime())[0]];
    }

    // ========== VARIABLES COMMUNES ==========

    const recallDateExtra = material.extras?.filter(extra => extra.name === "recallDate");
    const recallHourExtra = material.extras?.filter(extra => extra.name === "recallHour");
    const nextNotif = recallDateExtra?.length && moment().diff(moment(material.dSysMod).utc(true), "seconds") > 0 && !commonFunc.isBlank(recallDateExtra[0].value) && new Date(recallDateExtra[0].value).getFullYear() > 1970;
    const recallComplete = (nextNotif && recallHourExtra?.length ? (recallDateExtra[0].value.substring(0, 11) + recallHourExtra[0].value.substring(11, recallHourExtra[0].value.length))
        : (nextNotif ? recallDateExtra[0].value : null));

    // Variables projet
    const spSvcColor = material.service?.startColor || "#6C757D";
    const svcForeColor = material.service?.foreColor || "#000000";
    //const nbProprio = material.persons?.filter(person => person.client?.categoryID === 1);

    // Status pour affichage
    const displayStatus = material;
    const displayStatusID = material?.statusID;

    // ✅ FIX: Condition pour désactiver les boutons Email (comme l'original)
    const isEmailDisabled = material.statusID >= 3004;

    // ========== RENDER ==========
    const cardClass = `material-card material-${material.idMaterial}${statusClass} ${material.priority > 0 ? "priority" : ""} ${collapsed ? "collapsed" : ""}`;


    var supStyle = {};
    var leftColor = customStatusConfig[material.statusID]?.bgColor || "#6C757D";
    if (leftColor) supStyle.borderLeft = "4px solid " + leftColor;

    return (
        <div className={cardClass} style={{ ...supStyle }}>

            {/* ===== HEADER ===== */}
            <div className={"material-card-header"} onClick={() => setCollapsed(!collapsed)}>

                {/* Toggle Icon */}
                <span className="toggle-icon">
                    <i className={`fa fa-chevron-${collapsed ? "right" : "down"}`}></i>
                </span>

                {/* === MATERIAL ONLY: Priority Indicator === */}
                {material.priority > 0 && (
                    <div className="priority-indicator">
                        <span className="priority-dot"></span>
                        <span className="priority-label">Prioritaire</span>
                    </div>
                )}

                {/* Info principale */}
                <div className={"material-info"}>
                    <div className="material-title-row">
                        <div className="material-title">
                            {material.client?.name.toUpperCase()} {commonFunc.capitalize(material.client?.firstName)}
                        </div>
                        {material.service?.name && (
                            <span className="material-service-badge" style={{ backgroundColor: spSvcColor, color: svcForeColor }}>
                            {material.service.name}
                        </span>
                        )}

                        {nextNotif && recallComplete && (
                            <Badge className="ms-2" color="warning">
                                <i className="fa fa-clock me-1"></i>
                                A rappeler le {commonFunc.formatDateTime(moment(recallComplete).toDate())}
                            </Badge>
                        )}

                        {material.recall > 0 && (<span className="badge badge-primary material-recall">Relance n° {material.recall}{material.stepID > 0 ? (" - " + material.subStatus.name) : ""}</span>)}
                    </div>
                    <div className="material-contact">
                        <span className="material-name hidden">{material.title}</span>
                            {material.client?.tel && (
                                <span className="material-phone d-none d-md-block">
                                    <i className="fa fa-phone me-1"></i>
                                    {material.client.tel}
                                </span>
                            )}
                        {material.cRecommandation ? <span className="ms-2 text-muted"><i className={`fa ${altT("i18n:material.recoIcon" + material.cRecommandation.recommendedAction)}`}></i><i>{material.cRecommandation.reason}</i></span> : ""}
                    </div>
                </div>

                {/* Meta (droite) */}
                <div className={"material-meta"}>
                    <span className="material-association">{material.extSite?.name}</span>
                    <span className="material-date">
                        Modifié le {moment(material.dSysMod).format("DD/MM/YYYY")}
                    </span>
                </div>

                {/* === MATERIAL ONLY: Header Priority (collapsed) === */}
                {material.priority > 0 && (
                    <div className="header-priority">
                        <span className="header-priority-dot"></span>
                        <span className="header-priority-label">Prioritaire</span>
                    </div>
                )}

                {/* Header Status (collapsed) - commun */}
                {(collapsed && displayStatus) && (
                    <div className="header-status">
                        <span className={`header-status-icon ${displayStatus.status?.step >= 4 ? "completed" : "current"}`} style={{ backgroundColor: leftColor }}>
                            <i className={customStatusConfig[displayStatusID]?.icon || "fa fa-circle 2x"} style={{ color: "white" }} ></i>
                        </span>
                        <span className="header-status-label">
                            {customStatusConfig[displayStatusID]?.name || "En cours"}
                        </span>
                    </div>
                )}
            </div>

            {/* ===== CONTENU ===== */}
            <div className={"material-card-content"}>

                {/* Body - Actions + Fichiers */}
                <div className="material-card-body">

                    {/* Actions Section */}
                    {context.props.checkRights("MATERIAL", "S") && (
                        <div className="actions-section">
                            <div className="actions-title">
                                <i className="fa fa-bolt"></i>
                                Prochaines actions
                            </div>

                            <div className="action-buttons">
                                {(hasPrices && material.status?.step <= 2) ? (
                                    <button className="btn-action primary" onClick={() => context.manageShopOrder(material, context)}>
                                        <i className="fa fa-euro-sign"></i>
                                        Envoyer les tarifs
                                    </button>
                                ) : ""}
                                {(shouldVisit && (material.status?.step <= 2 || material.statusID === 3003)) ? (
                                    <button className="btn-action primary" onClick={() => dialogAltMaterial(context.props.i18nManager.t("material.scheduleVisit"), "SCHEDULEVISIT", material, context.materialModels, saveMaterial, saveCb, context.modalGenManager, context.props, context.props.i18nManager)}>
                                        <i className="fa fa-calendar"></i>
                                        Planifier visite
                                    </button>
                                ) : ""}
                                {(shouldVisit && material.statusID === 3004) ? (
                                    <button className="btn-action primary" onClick={() => context.updateMaterialStatus(3007, -1, "Visite réalisée", material.idMaterial)}>
                                        <i className="fa fa-map-marker-alt"></i>
                                        Visite réalisée
                                    </button>
                                ) : ""}
                                {(material.status?.step === 3 && material.statusID !== 3004) ? (
                                    <button className="btn-action warning" onClick={() => context.updateMaterialStatus(3008, -1, "Liste d'attente", material.idMaterial)}>
                                        <i className="fa fa-pause"></i>
                                        Liste d'attente
                                    </button>
                                ) : ""}
                                {(material.statusID === 3003 || material.statusID === 3007 || material.statusID === 3008) ? (
                                    <button className="btn-action primary" onClick={() => context.updateMaterialStatus(3005, -1, "Intervention Planifiée", material.idMaterial)}>
                                        <i className="fa fa-calendar-check"></i>
                                        Intervention Planifiée
                                    </button>
                                ) : ""}
                                {material.statusID === 3009 ? (
                                    <button className="btn-action primary" onClick={() => context.updateMaterialStatus(3005, -1, "Prospect transformé", material.idMaterial)}>
                                        <i className="fa fa-check"></i>
                                        Transformé Prospect
                                    </button>
                                ) : ""}
                                {material.status?.step < 5 ? (
                                    <button className="btn-action danger" onClick={() => dialogAltMaterial(context.props.i18nManager.t("material.materialloss"), "MATERIALLOSS", material, context.materialModels, saveMaterial, saveCb, context.modalGenManager, context.props, context.props.i18nManager)}>
                                        <i className="fa fa-ban"></i>
                                        Prospect Perdu
                                    </button>
                                ) : ""}
                                {/* Générer récap - déplacé ici */}
                                <button className="btn-action recap" onClick={() => context.generateMaterialRecap(material)}>
                                    <i className="fa fa-file-pdf"></i>
                                    Générer récap.
                                </button>
                                {/* Nouvelle relance - déplacé ici */}
                                {(material.status?.step <= 2 || material.statusID === 3003) ? (
                                    <button className="btn-action recap" onClick={() => context.newRecall(material)}>
                                        <i className="fa fa-arrow-rotate-right"></i>
                                        Nouvelle relance
                                    </button>
                                ) : ""}
                            </div>

                            {/* Quick Actions */}
                            <div className="quick-actions">
                                {(context.props.checkRights("MATERIAL", "A") && material.client?.tel) ? (
                                    <a href={`callto:${material.client.tel}`} className="quick-btn">
                                        <i className="fa fa-phone"></i>
                                        Appeler
                                    </a>
                                ) : ""}
                                {/* ✅ FIX: Email désactivé au lieu de masqué (comme l'original) */}
                                {context.props.checkRights("MATERIAL", "A") && (
                                    <button className="quick-btn" disabled={isEmailDisabled} onClick={() => sendEmailDialog(material, context.props.dataCfg, context.props.i18nManager)}>
                                        <i className="fa fa-envelope"></i>
                                        Email
                                    </button>
                                )}
                                {/* ✅ FIX: Email Récap désactivé au lieu de masqué (comme l'original) */}
                                {context.props.checkRights("MATERIAL", "A") && (
                                    <button className="quick-btn" disabled={isEmailDisabled} onClick={() => sendRecapEmailDialog(material, context.props.dataCfg, context.props.i18nManager)}>
                                        <i className="fa fa-envelope"></i>
                                        Email Récap.
                                    </button>
                                )}
                                {context.props.checkRights("MATERIAL", "U") && (material.status?.step < 4 && material.statusID !== 3004 && material.statusID !== 3007) && (
                                    <button className="quick-btn highlight" onClick={() => showDialogMaterial(altT(templatesWidgetList[0]?.name), templatesWidgetList[0]?.tag, material, context.materialModels, saveMaterial, saveCb, context.modalGenManager, context.props, context.props.i18nManager)}>
                                        <i className="fa fa-edit"></i>
                                        Modifier
                                    </button>
                                )}
                                {context.props.checkRights("MATERIAL", "CU") && material.status?.step < 4 && (
                                        <button className="quick-btn" onClick={() => showDialogMaterial(altT(templatesWidgetList[0]?.name?.replace("edit", "duplicate")), templatesWidgetList[0]?.tag, material, context.materialModels, saveMaterial, () => { context.getMaterials(3000, true, () => { }); }/*saveCb*/, context.modalGenManager, context.props, context.props.i18nManager, true)}>
                                        <i className="fa fa-copy"></i>
                                        Dupliquer
                                    </button>
                                )}
                                {context.props.checkRights("MATERIAL", "R") && (
                                    <button className="quick-btn" onClick={() => dialogAltMaterial(context.props.i18nManager.t("material.tabDetails"), "DLG_MATERIAL", material, context.materialModels, saveMaterial, saveCb, context.modalGenManager, context.props, context.props.i18nManager)}>
                                        <i className="fa fa-circle-info"></i>
                                        Consulter
                                    </button>
                                )}
                                {context.props.checkRights("MATERIAL_FLOWS", "L") && (
                                    <button className="quick-btn" onClick={() => context.showFlows(material.idMaterial, material)}>
                                        <i className="fa fa-clock-rotate-left"></i>
                                        Historique
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Files Section (Material) */}
                    {context.props.checkRights("MATERIAL_FILES", "L") && (
                        <div className="files-section">
                            <div className="card-header blue">
                                <span className="card-title">
                                    <i className="fa fa-folder me-2"></i>
                                    Fichiers
                                </span>
                                    <Button className="btn-icon rounded-circle m-0" size="sm" color="primary" style={{ width: "36px", height: "36px" }} onClick={() => { filesCustomRef.current.displayAddDialog(); }}>
                                    <i className="fa fa-plus me-1"></i>
                                </Button>
                            </div>
                            <div className="card-body">
                                <div className="files-group">
                                    <div className="files-group-title">Fichiers</div>
                                    <div className="files-list">
                                        <div className={`${filesAltObj.files?.length > 0 ? "" : "d-none"}`}>
                                            <FilesCustom
                                                ref={filesCustomRef}
                                                className={"material-files" + material.idMaterial}
                                                bindID={material.idMaterial}
                                                rightType={"MATERIAL_FILES"}
                                                checkRights={context.props.checkRights}
                                                fileTypeID="MATERIAL"
                                                src={material}
                                                srcName={material.title}
                                                withFilters
                                                    noUploadBtn
                                                shouldRender
                                                altRendering
                                                    noRemove={"PDF"}
                                                    noContextMenu={false}
                                                    noChooseTypes
                                                onlyDelete
                                                defaultFileType={"DVS"}
                                                {...filesAltObj}
                                                altFilesUrl="../api/material/GetMaterialFiles"
                                                altRemoveFilesUrl="../api/material/RemoveMaterialFile"
                                                altUploadFilesUrl="../api/material/UploadMaterialFiles"
                                                />
                                        </div>
                                        {filesAltObj.files?.length <= 0 && (
                                            <div className="empty-files">
                                                <i className="fa fa-inbox"></i>
                                                <span>Aucun document</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {filesRcpObj.files?.length > 0 && (
                                    <div className="files-group">
                                        <div className="files-group-title">Récapitulatif</div>
                                        <div className="files-list">>
                                            <FilesCustom
                                                className={"material-rcp-files" + material.idMaterial}
                                                bindID={material.idMaterial}
                                                rightType={"MATERIAL_FILES"}
                                                checkRights={context.props.checkRights}
                                                fileTypeID="MATERIAL"
                                                src={material}
                                                srcName={material.title}
                                                withFilters
                                                noUploadBtn
                                                shouldRender
                                                altRendering
                                                noRemove
                                                noContextMenu
                                                onlyDelete
                                                defaultFileType={"RCP"}
                                                {...filesRcpObj}
                                                altFilesUrl="../api/material/GetMaterialFiles"
                                                altRemoveFilesUrl="../api/material/RemoveMaterialFile"
                                                altUploadFilesUrl="../api/material/UploadMaterialFiles"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CardMat;